import { FC, useContext, useMemo, useState } from 'react';

import { H2, Preloader, Tooltip as TooltipKit } from '@smartshell/ui-kit';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { formatMoney } from '../utils';
import { LABEL_TRANSLATION_MAP } from './constants';
import { IChartDataItem, IRevenueProps } from './interfaces';
import s from './Revenue.module.scss';
import { RevenueTooltip } from './RevenueTooltip';

export const Revenue: FC<IRevenueProps> = ({
  labels,
  values,
  loading,
  sum,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const getChartData = (
    labels: string[] | undefined,
    values: number[] | undefined
  ): IChartDataItem[] => {
    if (!labels || !values) return [];

    return labels
      .map((label, index) => {
        if (label.includes('_percent')) return null;

        const key = label as keyof typeof LABEL_TRANSLATION_MAP;
        const translationKey = LABEL_TRANSLATION_MAP[key];

        if (!translationKey) return null;

        return {
          name: t(translationKey),
          value: values[index] || 0,
        };
      })
      .filter((item): item is { name: string; value: number } => !!item)
      .filter((item) => item.value !== 0);
  };

  const chartData = useMemo(
    () => getChartData(labels, values?.values),
    [labels, values, t]
  );

  const [focusBar, setFocusBar] = useState<number | null>(null);

  return (
    <div
      className={s.container}
      onMouseLeave={() => {
        setFocusBar(null);
      }}
    >
      <div className={s.header}>
        <H2 className={s.title}>{t('report.hosts.variant.proceeds')}</H2>
        <TooltipKit
          contentClassName={s.tooltip}
          content={t('cashless.tooltip')}
        />
      </div>
      {(labels === undefined || values?.values.every((val) => val === 0)) &&
      !loading ? (
        <div className={s.placeholder}>
          {values?.values.every((val) => val === 0)
            ? t('visitors.empty.data')
            : t('report.placeholder.emptyDates')}
        </div>
      ) : (
        <Preloader loading={loading}>
          <ResponsiveContainer className={s.data} width="100%" height="100%">
            <BarChart
              data={chartData}
              onMouseMove={(state) => {
                if (state.isTooltipActive) {
                  setFocusBar(state.activeTooltipIndex ?? null);
                } else {
                  setFocusBar(null);
                }
              }}
              onMouseLeave={() => {
                setFocusBar(null);
              }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                dy={8}
                tick={{ fill: 'var(--text-primary)' }}
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${value} ${currency.sign}`}
                axisLine={false}
                tickLine={false}
                width={130}
                dx={-55}
                tick={{
                  fill: 'var(--text-primary)',
                  style: { whiteSpace: 'nowrap' },
                }}
                fontSize={12}
              />
              <Tooltip
                cursor={{
                  fill: 'var(--bg-item-secondary)',
                }}
                content={({ payload }) => {
                  const rawValue = payload?.[0]?.value;
                  const value = typeof rawValue === 'number' ? rawValue : 0;
                  const label = payload?.[0]?.payload?.name;

                  const percentage =
                    !loading && typeof sum === 'number' && sum > 0
                      ? `${((value / sum) * 100).toFixed(2)}%`
                      : '-';

                  return (
                    <RevenueTooltip
                      title={String(label)}
                      text_1={t('amount.revenue')}
                      value_1={`${formatMoney(value, currency.sign)}`}
                      text_2={t('percentage.total.revenue')}
                      value_2={percentage}
                      variant="primary"
                    />
                  );
                }}
              />

              <Bar
                fill="var(--bg-blue-10)"
                dataKey="value"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="value"
                  position={'inside'}
                  content={(props) => {
                    const { x, y, width, value } = props;
                    const XPosition = Number(x) + Number(width) / 2;
                    const YPosition = Number(y) - 8;

                    return (
                      <text
                        x={XPosition}
                        y={YPosition}
                        fill="var(--text-primary)"
                        fontSize="18"
                        textAnchor="middle"
                      >
                        {value && formatMoney(value, currency.sign)}
                      </text>
                    );
                  }}
                />
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      focusBar === index
                        ? 'var(--bg-blue)'
                        : 'var(--bg-blue-10)'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Preloader>
      )}
    </div>
  );
};
